import { useEffect, useState } from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useUiEditorState } from "@/tiptap/hooks/use-ui-editor-state"
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"
import { useFloatingToolbarVisibility } from "@/tiptap/hooks/use-floating-toolbar-visibility"

// --- Node ---
import { ImageNodeFloating } from "@/tiptap/nodes/image-node/image-node-floating"

// --- Icons ---
import { MoreVerticalIcon } from "@/tiptap/icons/more-vertical-icon"

// --- UI ---
import { ColorTextPopover } from "@/tiptap/ui/color-text-popover"
import { FontSizePopover } from "@/tiptap/ui/font-size-popover"
// [AI DISABLED] import { ImproveDropdown } from "@/tiptap/ui/improve-dropdown"
import { LinkPopover } from "@/tiptap/ui/link-popover"
import { canToggleMark, MarkButton } from "@/tiptap/ui/mark-button"
import {
  canSetTextAlign,
  TextAlignButton,
} from "@/tiptap/ui/text-align-button"
import {
  canSetTextDirection,
  TextDirectionButton,
} from "@/tiptap/ui/text-direction-button"
import { TurnIntoDropdown } from "@/tiptap/ui/turn-into-dropdown"
import { IndentButton } from "@/tiptap/ui/indent-button"
import {
  canResetFormatting,
  ResetAllFormattingButton,
} from "@/tiptap/ui/reset-all-formatting-button"

// --- Utils ---
import { isSelectionValid } from "@/tiptap/lib/tiptap-collab-utils"

import { Button } from "@/tiptap/ui-primitive/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/tiptap/ui-primitive/popover"
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "@/tiptap/ui-primitive/toolbar"

// --- UI Utils ---
import { FloatingElement } from "@/tiptap/ui-utils/floating-element"

const MOBILE_TOOLBAR_BREAKPOINT = 768

export function NotionToolbarFloating() {
  const { editor } = useTiptapEditor()
  const isMobile = useIsBreakpoint("max", MOBILE_TOOLBAR_BREAKPOINT)
  const { lockDragHandle, commentInputVisible } =
    useUiEditorState(editor)
  // [AI DISABLED] Add aiGenerationActive back to extraHideWhen when enabling AI:

  const { shouldShow } = useFloatingToolbarVisibility({
    editor,
    isSelectionValid,
    extraHideWhen: Boolean(commentInputVisible),
  })

  if (lockDragHandle || isMobile) return null

  return (
    <FloatingElement shouldShow={shouldShow}>
      <Toolbar variant="floating">
        <FormattingToolbarGroups hideWhenUnavailable={true} />
      </Toolbar>
    </FloatingElement>
  );
}

/**
 * Formatting controls shared by the floating toolbar and the pinned
 * header toolbar. The pinned toolbar passes hideWhenUnavailable={false} so
 * buttons stay in place (disabled) instead of appearing and disappearing.
 */
export function FormattingToolbarGroups({ hideWhenUnavailable = true, popoverSide = "top" }) {
  return (
    <>
      {/* [AI DISABLED] <ToolbarGroup><ImproveDropdown hideWhenUnavailable={hideWhenUnavailable} /></ToolbarGroup><ToolbarSeparator /> */}

      <ToolbarGroup>
        <TurnIntoDropdown hideWhenUnavailable={hideWhenUnavailable} />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" hideWhenUnavailable={hideWhenUnavailable} />
        <MarkButton type="italic" hideWhenUnavailable={hideWhenUnavailable} />
        <MarkButton type="underline" hideWhenUnavailable={hideWhenUnavailable} />
        <MarkButton type="strike" hideWhenUnavailable={hideWhenUnavailable} />
        <MarkButton type="code" hideWhenUnavailable={hideWhenUnavailable} />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <ImageNodeFloating />
      </ToolbarGroup>

      <ToolbarGroup>
        <LinkPopover autoOpenOnLinkActive={false} hideWhenUnavailable={hideWhenUnavailable} />
        <ColorTextPopover hideWhenUnavailable={hideWhenUnavailable} />
        <FontSizePopover hideWhenUnavailable={hideWhenUnavailable} />
      </ToolbarGroup>

      <MoreOptions hideWhenUnavailable={hideWhenUnavailable} popoverSide={popoverSide} />
    </>
  );
}

function canMoreOptions(editor) {
  if (!editor) {
    return false
  }

  const canTextAlignAny = ["left", "center", "right", "justify"].some((align) =>
    canSetTextAlign(editor, align))

  const canTextDirectionAny = ["ltr", "rtl"].some((direction) =>
    canSetTextDirection(editor, direction))

  const canMarkAny = ["superscript", "subscript"].some((type) =>
    canToggleMark(editor, type))

  return canMarkAny || canTextAlignAny || canTextDirectionAny || canResetFormatting(editor)
}

function shouldShowMoreOptions(params) {
  const { editor, hideWhenUnavailable } = params

  if (!editor) {
    return false
  }

  if (hideWhenUnavailable && !editor.isActive("code")) {
    return canMoreOptions(editor);
  }

  return Boolean(editor?.isEditable);
}

export function MoreOptions({
  editor: providedEditor,
  hideWhenUnavailable = false,
  popoverSide = "top",
  ...props
}) {
  const { editor } = useTiptapEditor(providedEditor)
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setShow(shouldShowMoreOptions({
        editor,
        hideWhenUnavailable,
      }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable])

  if (!show || !editor || !editor.isEditable) {
    return null
  }

  return (
    <>
      <ToolbarSeparator />
      <ToolbarGroup>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              role="button"
              tabIndex={-1}
              tooltip="More options"
              {...props}>
              <MoreVerticalIcon className="tiptap-button-icon" />
            </Button>
          </PopoverTrigger>

          <PopoverContent side={popoverSide} align="end" alignOffset={4} sideOffset={4} asChild>
            <Toolbar variant="floating" tabIndex={0}>
              <ToolbarGroup>
                <MarkButton type="superscript" />
                <MarkButton type="subscript" />
              </ToolbarGroup>

              <ToolbarSeparator />

              <ToolbarGroup>
                <TextAlignButton align="left" />
                <TextAlignButton align="center" />
                <TextAlignButton align="right" />
                <TextAlignButton align="justify" />
                <TextDirectionButton direction="ltr" />
                <TextDirectionButton direction="rtl" />
              </ToolbarGroup>

              <ToolbarSeparator />

              <ToolbarGroup>
                <IndentButton action="outdent" />
                <IndentButton action="indent" />
              </ToolbarGroup>

              <ToolbarSeparator />

              <ToolbarGroup>
                <ResetAllFormattingButton />
              </ToolbarGroup>
            </Toolbar>
          </PopoverContent>
        </Popover>
      </ToolbarGroup>
    </>
  );
}
